import React from 'react';
import { FiCamera, FiFeather, FiBook, FiDollarSign, FiClock, FiTrendingUp } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

interface HomePageProps {
  setCurrentPage: (page: 'home' | 'upload' | 'collection' | 'about' | 'resources' | 'loading' | 'chat' | 'settings') => void;
  userRole: string;
  setUserRole: (role: string) => void;
}

function HomePage({ setCurrentPage, userRole, setUserRole }: HomePageProps) {
  const { isAuthenticated, currentUser } = useAuth();
  const IconCamera = FiCamera as any;
  const IconFeather = FiFeather as any;
  const IconBook = FiBook as any;
  const IconDollarSign = FiDollarSign as any;
  const IconClock = FiClock as any;
  const IconTrendingUp = FiTrendingUp as any;

  if (isAuthenticated) {
    return (
      <section className="home-section logged-in">
        <div className="container">
          <div className="welcome-header" style={{ marginBottom: '2rem', textAlign: 'center' }}>
            <h1>Welcome back{currentUser?.name ? `, ${currentUser.name}` : ''}!</h1>
            <p className="subtitle">Ready to continue your conservation journey?</p>
          </div>

          <div className="quick-actions-grid">
            <button 
              className="action-card" 
              onClick={() => setCurrentPage('upload')}
            >
              <IconCamera className="action-icon" size="1em" />
              <h3>Scan New Plant</h3>
              <p>Identify invasive species instantly</p>
            </button>
            
            <button 
              className="action-card" 
              onClick={() => setCurrentPage('collection')}
            >
              <IconFeather className="action-icon" size="1em" />
              <h3>My Collection</h3>
              <p>View your identified plants</p>
            </button>
            
            <button 
              className="action-card" 
              onClick={() => setCurrentPage('resources')}
            >
              <IconBook className="action-icon" size="1em" />
              <h3>Learn More</h3>
              <p>Explore the database</p>
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      {/* Hero Section */}
      <section className="hero enhanced">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title enhanced">Identify Invasive Plants Instantly</h1>
              <p className="subtitle hero-subtitle enhanced">
                Identify, Research, Portfolio
              </p>
            </div>

            <div className="hero-action">
              <button
                className="button hero-button enhanced"
                onClick={() => setCurrentPage('upload')}
              >
                <span className="button-icon"></span>
                <span className="button-text">START SCANNING</span>
              </button>

              <p className="region-hint-text">
                 Start scanning now to identify and protect your local ecosystem
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Economic Impact Panel - "Why This Matters" */}
      <section className="impact-section" style={{ backgroundColor: 'var(--bg-secondary)', padding: '4rem 0' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why This Matters</h2>
            <p className="section-subtitle">Economic and Ecological Consequences of Invasive Species</p>
          </div>
          
          <div className="impact-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginTop: '2rem' }}>
            <div className="impact-card" style={{ background: 'var(--container-bg)', padding: '2rem', borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-sm)' }}>
              <IconDollarSign className="impact-icon" style={{ fontSize: '2.5rem', marginBottom: '1rem' }} />
              <h3>Economic Cost</h3>
              <p>Invasive species cost the global economy billions annually in control measures, lost agricultural yield, and infrastructure damage. Early detection significantly reduces these long-term costs.</p>
            </div>
            
            <div className="impact-card" style={{ background: 'var(--container-bg)', padding: '2rem', borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-sm)' }}>
              <IconClock className="impact-icon" style={{ fontSize: '2.5rem', marginBottom: '1rem' }} />
              <h3>Early Action</h3>
              <p>Detecting invasive plants before they establish large populations is the most cost-effective management strategy. Every scan contributes to this early warning system.</p>
            </div>
            
            <div className="impact-card" style={{ background: 'var(--container-bg)', padding: '2rem', borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-sm)' }}>
              <IconTrendingUp className="impact-icon" style={{ fontSize: '2.5rem', marginBottom: '1rem' }} />
              <h3>Ecosystem Health</h3>
              <p>Native biodiversity is essential for resilient ecosystems. Invasive plants outcompete native flora, disrupting food webs and reducing habitat for local wildlife.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section enhanced">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Features</h2>
            <p className="section-subtitle">Everything you need to identify and manage invasive plants</p>
          </div>

          <div className="features-grid enhanced">
            <div className="feature-card enhanced">
              <div className="feature-icon"></div>
              <h3 className="feature-title">AI-Powered Recognition</h3>
              <p className="feature-description">
                Multimodal LLLMs for plant image classification
              </p>
              <div className="feature-badges">
                <span className="feature-badge">Real-time</span>
                <span className="feature-badge">High Accuracy</span>
              </div>
            </div>

            <div className="feature-card enhanced">
              <div className="feature-icon"></div>
              <h3 className="feature-title">Comprehensive Database</h3>
              <p className="feature-description">
                Access detailed information about identified plants, including control methods, ecological impact, and native alternatives.
              </p>
              <div className="feature-badges">
                <span className="feature-badge">Detailed Info</span>
                <span className="feature-badge">Control Methods</span>
              </div>
            </div>

            <div className="feature-card enhanced">
              <div className="feature-icon"></div>
              <h3 className="feature-title">Conservation Mapping</h3>
              <p className="feature-description">
                Contribute to conservation efforts by mapping invasive species distribution and helping researchers track their spread.
              </p>
              <div className="feature-badges">
                <span className="feature-badge">Data Sharing</span>
                <span className="feature-badge">Research</span>
              </div>
            </div>

            <div className="feature-card enhanced">
              <div className="feature-icon"></div>
              <h3 className="feature-title">Mobile Optimized</h3>
              <p className="feature-description">
                Designed for field use with offline capabilities, GPS integration, and camera-optimized image capture.
              </p>
              <div className="feature-badges">
                <span className="feature-badge">Offline Mode</span>
                <span className="feature-badge">GPS Ready</span>
              </div>
            </div>

            <div className="feature-card enhanced">
              <div className="feature-icon"></div>
              <h3 className="feature-title">Privacy Protected</h3>
              <p className="feature-description">
                Your data is secure with encrypted storage and optional anonymous contribution to scientific research.
              </p>
              <div className="feature-badges">
                <span className="feature-badge">Encrypted</span>
                <span className="feature-badge">Anonymous</span>
              </div>
            </div>

            <div className="feature-card enhanced">
              <div className="feature-icon"></div>
              <h3 className="feature-title">Local Scope</h3>
              <p className="feature-description">
                Made for Central Texas scope
              </p>
              <div className="feature-badges">
                <span className="feature-badge">Multi-region</span>
                <span className="feature-badge">Local Data</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Make a Difference?</h2>
            <p className="cta-subtitle">
              Join our community of citizen scientists and help protect native ecosystems from invasive species.
            </p>
            <button
              className="button cta-button"
              onClick={() => setCurrentPage('upload')}
            >
              <span className="button-icon"></span>
              <span className="button-text">Start Identifying Plants</span>
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

export default HomePage;
